<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <div class="q-gutter-md">
      <banner
        v-if="isUpdatingWorkflows"
        show-icon
        :message="$t('run.done.textAnalyzing')"
      />
      <banner
        v-else-if="isDoneInOffline"
        show-icon
        :message="$t('run.done.doneInOfflineMode')"
      />

      <session-details-card
        v-if="hasActiveSession && !isParent"
        ref="sessionCard"
        :session="activeSession"
        :open-session-activity="sessionActivityId"
        :all-programs="allPrograms"
        :client="activeSessionClient"
        status-changes="targetStatusChanges"
        editable
        apply-immediately
        @changed="onSessionChanged"
        @delete-session="onDeleteSession"
      />
      <h5 v-else-if="isParent">
        {{ $t('run.done.headingSessionFinished') }}
      </h5>
    </div>
  </q-page>
</template>
<script>
import SessionDetailsCard from 'components/Sessions/SessionDetailsCard';
import Banner from 'components/UI/Banner';
import { chain, get, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useClients } from 'src/composables/clients';
import Activities from 'src/mixins/Activities';
import Targets from 'src/mixins/Targets';
import { auth } from 'src/store/auth';
import { programs } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { sessions } from 'src/store/sessions';
import { userInterface } from 'src/store/userInterface';

export default {
  name: 'Done',

  components: { Banner, SessionDetailsCard },

  mixins: [Targets, Activities],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      try {
        vm.onSessionChanged(false);
        if (vm.$route.params.sessionActivityId) {
          vm.setActiveSessionId(vm.$route.params.sessionActivityId);
        }
        if (get(vm.activeSession, 'id') !== vm.$route.params.sessionId) {
          await vm.loadActiveSession({ sessionId: vm.$route.params.sessionId });
        }

        await vm.loadDeletedSessionPrograms(vm.$route.params.sessionId);
      } catch (err) {
        console.error(err);
        vm.$router.push('/');
      }
    });
  }, // beforeRouteEnter()

  async beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges && !this.isAuthSessionExpired) {
      const leaveConfirmation = window.confirm(
        this.$t('history.view.confirmLeave'),
      );

      if (!leaveConfirmation) {
        if (this.activeSessionClient?.id !== this.currentClient.id) {
          await this.changeCurrentClient(this.activeSessionClient);
        }
        next(false);
        return;
      }
    }

    this.$q.loading.show();
    try {
      await this.finishActiveSession();
    } finally {
      this.$q.loading.hide();
    }

    next();
  },

  setup() {
    const { activeSessionClient } = useClients();
    return { activeSessionClient };
  },

  data() {
    return {
      isSaving: false,
      unsavedChanges: false,
      sessionActivityId: null,
      sessionClient: {},
    };
  }, // beforeRouteLeave()

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'isParent', 'isAuthSessionExpired']),
    ...mapState(programs, ['allPrograms']),
    ...mapState(sessions, ['activeSession']),

    hasActiveSession() {
      return !isEmpty(this.activeSession);
    }, // hasActiveSession()

    isUpdatingWorkflows() {
      return get(this.activeSession, 'status', 'done') === 'processing';
    }, // isUpdatingWorkflows()

    isDoneInOffline() {
      return (this.activeSession?.status || 'done') === 'offline-done';
    },

    targetStatusChanges() {
      return get(this.activeSession, 'targetStatusChanges', []);
    }, // targetStatusChanges()

  },

  watch: {
    'activeSession.status': {
      immediate: false,
      handler(newStatus, prevStatus) {
        if ((prevStatus !== 'processing') || (newStatus !== 'done')) {
          return;
        }
        const invalidateActions = chain(this.targetStatusChanges)
          .map('program')
          .uniq()
          .map((programId) => this.invalidateProgramTargetsCache({ program: programId }))
          .value();
        Promise.all(invalidateActions);
      },
    },
  },

  methods: {
    ...mapActions(auth, ['changeCurrentClient']),
    ...mapActions(sessions, ['loadActiveSession', 'finishActiveSession']),
    ...mapActions(userInterface, ['openLeftMenuDrawer']),
    ...mapActions(programsTargets, ['invalidateProgramTargetsCache']),
    ...mapActions(programs, ['loadDeletedSessionPrograms']),

    onSessionChanged(isChanged) {
      this.unsavedChanges = isChanged;
    },

    onDeleteSession() {
      this.unsavedChanges = false;
      this.$router.push({ name: 'Home' });
    },

    setActiveSessionId(sessionActivityId) {
      this.sessionActivityId = sessionActivityId;
    },

  },

};
</script>
